import React from 'react';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import type { PanelLevel } from './types';

interface RootItem {
  level: PanelLevel;
  label: string;
  icon: IconName;
  badge?: number;
  testid: string;
}

interface PanelRootProps {
  name: string;
  onNameChange: (value: string) => void;
  globalGroups: string[];
  pagesCount: number;
  databasesCount: number;
  domainsCount: number;
  onOpen: (level: PanelLevel) => void;
}

/** Nível raiz: nome do website + grupos da config global (dinâmico) + Páginas/Bancos/Domínios. */
export const PanelRoot: React.FC<PanelRootProps> = ({
  name, onNameChange, globalGroups, pagesCount, databasesCount, domainsCount, onOpen,
}) => {
  const items: RootItem[] = [
    ...globalGroups.map((group): RootItem => ({
      level: { kind: 'global-group', group },
      label: group,
      icon: 'sliders',
      testid: `panel-item-group-${group}`,
    })),
    { level: { kind: 'pages' }, label: 'Páginas', icon: 'file-text', badge: pagesCount, testid: 'panel-item-pages' },
    { level: { kind: 'databases' }, label: 'Bancos de Dados', icon: 'database', badge: databasesCount, testid: 'panel-item-databases' },
    { level: { kind: 'domains' }, label: 'Domínios', icon: 'globe', badge: domainsCount, testid: 'panel-item-domains' },
  ];

  return (
    <div className="space-y-4">
      <FormField label="Nome do website" required>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Nome do website"
          data-testid="customizer-name-input"
        />
      </FormField>

      <div className="space-y-1">
        {items.map((item) => (
          <button
            key={item.testid}
            type="button"
            onClick={() => onOpen(item.level)}
            className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-sm font-medium transition-colors text-left"
            style={{ color: 'var(--theme-card-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={item.testid}
          >
            <Icon name={item.icon} className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge !== undefined && (
              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-muted-text)' }}>
                {item.badge}
              </span>
            )}
            <Icon name="chevron-right" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
          </button>
        ))}
      </div>
    </div>
  );
};
